import { cn } from "@/shared/lib/cn";
import type { PlatformProfile } from "../mockData";

type Status = "ok" | "warn" | "bad";

function badge(s: Status) {
  return s === "ok"
    ? { bg: "#EAF3DE", color: "#27500A" }
    : s === "warn"
    ? { bg: "#FAEEDA", color: "#633806" }
    : { bg: "#FCEBEB", color: "#791F1F" };
}

function FraudRow({ label, value, status }: { label: string; value: string; status: Status }) {
  const b = badge(status);
  return (
    <div className="flex items-center justify-between py-2 border-b border-ink-12/50 last:border-b-0">
      <span className="text-[12px] text-ink-60">{label}</span>
      <span className="text-[10px] font-[500] px-2 py-0.5 rounded-full" style={{ background: b.bg, color: b.color }}>
        {value}
      </span>
    </div>
  );
}

function pctStatus(pct: number, okT: number, warnT: number): Status {
  return pct >= okT ? "ok" : pct >= warnT ? "warn" : "bad";
}

// ─── Shared insight ───────────────────────────────────────────────────────────

type InsVariant = "ig" | "ib" | "ia";
const INS_COLOR: Record<InsVariant, string> = { ig: "#639922", ib: "#378ADD", ia: "#BA7517" };

function ShieldSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
    </svg>
  );
}
function WarnSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
    </svg>
  );
}
function SparklesSvg({ size }: { size: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z"/>
    </svg>
  );
}

function Ins({ variant, text }: { variant: InsVariant; text: string }) {
  const Icon = variant === "ig" ? SparklesSvg : variant === "ia" ? WarnSvg : ShieldSvg;
  const iconColor = INS_COLOR[variant];
  return (
    <div className="flex items-start gap-[5px] px-[10px] py-[7px] rounded-md mt-[8px] text-[11px]"
      style={{ background: "rgba(28,28,28,0.04)", color: "rgba(28,28,28,0.55)" }}>
      <span style={{ color: iconColor, flexShrink: 0, marginTop: 1 }}>
        <Icon size={12} />
      </span>
      <span className="leading-snug">{text}</span>
    </div>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return <div className="text-[10px] font-display font-bold uppercase tracking-wider text-ink-40 mb-1.5">{children}</div>;
}

// ─── Reachability block ───────────────────────────────────────────────────────

function ReachabilityBlock({ reach24h, reach48h, reach72h, platform }: {
  reach24h: number; reach48h: number; reach72h: number; platform: string;
}) {
  const isTg = platform === "tg";
  return (
    <div>
      <SectionLabel>Досягаемость (% читают посты)</SectionLabel>
      <div className="grid grid-cols-3 gap-2">
        {[
          { label: "за 24 ч", value: reach24h },
          { label: "за 48 ч", value: reach48h },
          { label: "за 72 ч", value: reach72h },
        ].map(({ label, value }) => (
          <div key={label} className="rounded-lg p-3 text-center" style={{ background: "rgba(28,28,28,0.04)" }}>
            <div className="font-display font-[500] text-[18px] leading-tight">{value}%</div>
            <div className="text-[10px] text-ink-40 mt-0.5">{label}</div>
          </div>
        ))}
      </div>
      <Ins
        variant={isTg ? "ig" : "ib"}
        text={isTg
          ? "TG-аудитория читает быстро — формат 1/24 даёт максимальный охват за сутки."
          : "Аудитория читает медленнее TG — рекомендован формат 2/48 или 3/72 для максимального охвата."}
      />
    </div>
  );
}

// ─── Root ─────────────────────────────────────────────────────────────────────

export function FraudTab({ data }: { data: PlatformProfile }) {
  const liveStatus = pctStatus(data.liveAudiencePct, 80, 60);
  const suspStatus: Status = data.suspiciousPct <= 10 ? "ok" : data.suspiciousPct <= 20 ? "warn" : "bad";
  const botStatus: Status = data.botsPct <= 8 ? "ok" : data.botsPct <= 15 ? "warn" : "bad";
  const allOk = liveStatus === "ok" && suspStatus === "ok" && botStatus === "ok"
    && data.organicGrowth === "ok" && data.massFollowing === "ok";
  const activityAllOk = data.viewsManipulation === "ok" && data.reactionsManipulation === "ok";

  return (
    <div className="grid gap-6">
      {/* Качество аудитории */}
      <div>
        <SectionLabel>Качество аудитории</SectionLabel>
        <div className="bg-white rounded-md border border-ink-12 px-4 divide-y divide-ink-12/50">
          <FraudRow label="Живые подписчики"          value={`${data.liveAudiencePct}%`}   status={liveStatus} />
          <FraudRow label="Подозрительные подписчики" value={`${data.suspiciousPct}%`}      status={suspStatus} />
          <FraudRow label="Боты"                      value={`${data.botsPct}%`}             status={botStatus} />
          <FraudRow label="Органический рост"         value={data.organicGrowth === "ok" ? "Стабильный" : "Стагнация"} status={data.organicGrowth} />
          <FraudRow label="Масс-фолловинг"            value={data.massFollowing === "ok" ? "Не выявлен" : "Единично"}  status={data.massFollowing} />
        </div>
        <Ins
          variant={allOk ? "ig" : "ia"}
          text={allOk
            ? "Профиль чистый — подозрительной активности не обнаружено."
            : "Есть незначительные признаки — рекомендуем дополнительную проверку."}
        />
      </div>

      {/* Активность канала */}
      <div>
        <SectionLabel>Активность канала</SectionLabel>
        <div className="bg-white rounded-md border border-ink-12 px-4 divide-y divide-ink-12/50">
          <FraudRow label="Накрутка просмотров"     value={data.viewsManipulation === "ok" ? "Не выявлена" : "Замечена"} status={data.viewsManipulation} />
          <FraudRow label={data.key === "vk" ? "Накрутка лайков" : "Накрутка реакций"} value={data.reactionsManipulation === "ok" ? "Не выявлена" : "Замечена"} status={data.reactionsManipulation} />
          <FraudRow label="ER рекламных постов"    value={`${(data.er * 0.85).toFixed(1)}%`} status="ok" />
          <FraudRow label="Последний пост"          value="2 дня назад"                      status="ok" />
        </div>
        <Ins
          variant={activityAllOk ? "ig" : "ia"}
          text={activityAllOk
            ? "Активность органическая — резких аномалий в динамике не зафиксировано."
            : "Обнаружены незначительные аномалии — рекомендуем дополнительную проверку."}
        />
      </div>

      <ReachabilityBlock reach24h={data.reach24h} reach48h={data.reach48h} reach72h={data.reach72h} platform={data.key} />
    </div>
  );
}
